import { CITY_CATALOG_A } from "./cities-catalog-a-data";
import { CITY_CATALOG_B } from "./cities-catalog-b-data";
import { CITY_CATALOG_C } from "./cities-catalog-c-data";
import { CITY_CATALOG_D } from "./cities-catalog-d-data";
import type { CityData } from "./cities-data.types";

// Re-exported so existing `import { CityData } from "./cities-data"` consumers
// keep working; the canonical definition lives in the leaf module to avoid the
// catalog↔aggregator import cycle (see cities-data.types.ts).
export type { CityData } from "./cities-data.types";

export const CITIES: CityData[] = [
  ...CITY_CATALOG_A,
  ...CITY_CATALOG_B,
  ...CITY_CATALOG_C,
  ...CITY_CATALOG_D,
];

export const CITY_MAP: Record<string, CityData> = Object.fromEntries(
  CITIES.map((c) => [c.slug, c])
);

export const CITY_SLUGS = CITIES.map((c) => c.slug);

/**
 * Build-time pre-render cohort for the city timing pages. ALL indexable cities
 * are served + indexed (see `cityIsIndexable`), but only this top-demand cohort
 * is statically generated at build; every other city renders on-demand and is
 * ISR-cached (dynamicParams default). This keeps the build bounded (~36 pages
 * instead of ~600) while the full set still ranks. Ordered by city-timing search
 * demand so the highest-value pages are warm immediately after a deploy.
 */
export const PREBUILD_CITY_SLUGS: readonly string[] = [
  "mumbai",
  "new-delhi",
  "jaipur",
  "indore",
  "surat",
  "bangalore",
  "kolkata",
  "lucknow",
  "pune",
  "bhopal",
  "vadodara",
  "rajkot",
];

// ── Content helpers — used by [city]/page.tsx to fill template for thin cities ──

export function estimateSunriseRange(lat: number, lon: number): string {
  // IST meridian = 82.5°E; 4 min/degree longitude offset
  // Seasonal variation ≈ ±(|lat| / 30) × 50 min from mean
  const lonOffsetMin = Math.round((82.5 - lon) * 4);
  const variation = Math.round((Math.abs(lat) / 30) * 50);
  const baseMins = 6 * 60; // ~6:00 AM at equator on IST meridian
  const fmt = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${String(min).padStart(2, "0")} AM`;
  };
  return `${fmt(Math.round(baseMins + lonOffsetMin - variation))}–${fmt(Math.round(baseMins + lonOffsetMin + variation))}`;
}

export function getCitySunriseRange(city: CityData): string {
  return city.sunrise_range ?? estimateSunriseRange(city.latitude, city.longitude);
}

export function getCityRegionalNote(city: CityData): string {
  if (city.regional_note) return city.regional_note;
  const sr = getCitySunriseRange(city);
  return `${city.name}, ${city.state} follows Indian Standard Time (UTC+5:30). Sunrise ranges from ${sr.split("–")[0]} in summer to ${sr.split("–")[1]} in winter, calculated for ${city.name}'s exact coordinates (${city.latitude.toFixed(2)}°N, ${city.longitude.toFixed(2)}°E). The panchang, choghadiya, and rahu kaal timings above use these coordinates via Swiss Ephemeris for accurate daily muhurat timings. Local religious observances follow the Hindu lunisolar calendar as practised across ${city.state}.`;
}

export function getCityLocalFestivals(city: CityData): string[] {
  return city.local_festivals ?? ["Diwali", "Navratri", "Holi"];
}

/**
 * A city timing page is INDEXABLE when it has genuine per-city content — a
 * regional note AND local festivals — on top of the server-rendered engine data
 * (sunrise/tithi/choghadiya/rahu-kaal computed from the city's coordinates, the
 * Drik-Panchang model). The engine data is the unique content that makes each
 * page non-duplicate (rule 49), so the data-rich predicate is now safe at scale
 * (it was held out 2026-06-03 only while the data rendered client-side).
 *
 * Watched post-deploy by the indexable-value gate (weekly-seo-index-protection),
 * the render-contract gate (rule 49), and the impression-regression guard
 * (rule 48). REVERSIBLE: tighten this predicate (e.g. back to a `PREBUILD_CITY_SLUGS`
 * allowlist) to pull cities out of the index.
 */
export function cityIsIndexable(city: CityData): boolean {
  return Boolean(city.regional_note && city.local_festivals && city.local_festivals.length > 0);
}

// Only explicitly promoted cities are statically generated and exposed in SEO
// link grids/sitemaps. Other city pages still serve the calculator on demand.
export const SEO_CITIES = CITIES.filter(cityIsIndexable);

export const SEO_CITY_SLUGS = SEO_CITIES.map((c) => c.slug);

export const INDEXABLE_CITY_SLUGS: ReadonlySet<string> = new Set(SEO_CITY_SLUGS);
